
import React from 'react';
import { Property } from '../types';
import { Layers, MapPin, EyeOff, Tag } from 'lucide-react';

interface PropertyCardProps {
  property: Property;
  agentAvatar?: string;
  onPropertyClick: (property: Property) => void;
  onAgentClick?: (agentId: string) => void;
  isOffline?: boolean;
}

export const PropertyCard: React.FC<PropertyCardProps> = ({ property, agentAvatar, onPropertyClick, onAgentClick, isOffline }) => {
  const priceFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: property.currency || 'USD',
    maximumFractionDigits: 0,
  });

  const getPropertyTag = () => {
    if (property.type === 'Land') return 'Land';
    if (property.bedrooms) return `${property.bedrooms} Bed ${property.type}`;
    return property.type;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Sale': return 'bg-blue-600/90';
      case 'Rent': return 'bg-green-600/90';
      case 'Short Let': return 'bg-amber-600/90';
      case 'JV': return 'bg-purple-600/90';
      default: return 'bg-black/60';
    }
  };

  const getPriceColorClass = (category: string) => {
    switch (category) {
      case 'Sale': return 'text-blue-400';
      case 'Rent': return 'text-green-400';
      case 'Short Let': return 'text-orange-400';
      case 'JV': return 'text-purple-400';
      default: return 'text-white';
    }
  };

  const handleAgentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAgentClick) {
      onAgentClick(property.agent_id);
    }
  };

  return (
    <div 
      className="group relative aspect-square bg-gray-100 dark:bg-zinc-900 overflow-hidden cursor-pointer active:opacity-95 transition-all shadow-sm"
      onClick={() => onPropertyClick(property)}
    >
      <img 
        src={property.images[0]} 
        alt={property.title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        loading="lazy"
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 opacity-80 group-hover:opacity-100 transition-opacity" />

      {/* Top Overlays */}
      <div className="absolute top-2 left-2 right-2 flex justify-between items-start z-10">
        <div className="flex flex-col gap-1 items-start max-w-[70%]">
          {/* CATEGORY TAG (For Sale, For Rent etc) */}
          <span className={`px-2 py-0.5 rounded-md text-[7px] md:text-[8px] font-black uppercase tracking-widest text-white shadow-sm flex items-center gap-1 ${getCategoryColor(property.category)} backdrop-blur-sm`}>
            {property.category}
          </span>
          <span className="px-2 py-0.5 bg-white/90 dark:bg-black/80 backdrop-blur-md rounded-md text-[7px] md:text-[8px] font-black uppercase tracking-wider text-gray-900 dark:text-white shadow-sm truncate w-full">
            {getPropertyTag()}
          </span>
        </div>
        
        {/* Agent Profile Icon in TOP Corner */}
        {agentAvatar && (
          <div 
            onClick={handleAgentClick}
            className="w-6 h-6 md:w-8 md:h-8 rounded-full border-2 border-white dark:border-zinc-800 shadow-xl overflow-hidden flex-shrink-0 hover:scale-110 transition-transform active:scale-90 bg-white"
          >
            <img src={agentAvatar} className="w-full h-full object-cover" alt="Agent" />
          </div>
        )}
      </div>

      <div className="absolute top-10 right-2 flex flex-col gap-1 items-end z-10">
        {property.images.length > 1 && (
          <div className="bg-white/20 backdrop-blur-sm p-1 rounded-md text-white border border-white/20">
            <Layers className="w-3 h-3" />
          </div>
        )}
        {isOffline ? (
          <span className="px-1.5 py-0.5 rounded text-[7px] font-black uppercase tracking-widest bg-red-600 text-white flex items-center gap-1 shadow-lg">
            <EyeOff className="w-2.5 h-2.5" /> Private
          </span>
        ) : (
          property.status !== 'Available' && (
            <span className={`px-1.5 py-0.5 rounded text-[7px] font-black uppercase tracking-widest ${
              property.status === 'Sold' ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'
            }`}>
              {property.status}
            </span>
          )
        )}
      </div>

      {/* Bottom Information */}
      <div className="absolute bottom-2 left-2 right-2 z-10">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-1 text-white/90">
            <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
            <span className="text-[9px] font-bold truncate leading-none tracking-tight">
              {property.location}
            </span>
          </div>
          <div className={`text-sm font-black leading-none tracking-tight ${getPriceColorClass(property.category)}`}>
            {priceFormatter.format(property.price)}
          </div>
        </div>
      </div>

      <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
    </div>
  );
};
